// src/pages/Toolbox.tsx — Toolbox Talks & WHS Notifications
// Follows frontend-skill.md conventions exactly:
//   - Functional components + hooks only
//   - Tailwind utility classes only
//   - Primary: indigo-600  Success: green-500  Warning: amber-500  Danger: red-500
//   - Neutrals: slate-100 through slate-900

import { useState, useMemo, useEffect } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_WHS_NOTIFICATIONS, WHSNotification, NotificationSeverity, NotificationAudience } from '../utils/mockDataWHS'
import { MOCK_EMPLOYEES } from '../utils/mockDataExtended'
import { MOCK_JOBS } from '../utils/mockData'
import { formatDate, formatRelative } from '../utils/formatters'
import { getToolboxDrafts, activateToolboxDraft, type ToolboxDraft } from '../utils/mockDataProjectGaps'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchToolboxTalks } from '../services/toolboxService'
import {
  Bell, Plus, X, CheckCircle, AlertTriangle, Clock, Search,
  Users, ChevronRight, MessageSquare, Send, ArrowLeft,
  BookOpen, ThumbsUp, Smartphone, Megaphone, RefreshCw, Zap, Shield, Bot, Download,
} from 'lucide-react'

// ─────────────────────────────────────────────────────────────────
// LOCAL TYPES
// ─────────────────────────────────────────────────────────────────

interface ToolboxAttendee {
  id: string
  workerId: string
  workerName: string
  workerType: 'EMPLOYEE' | 'SUBCONTRACTOR'
  company?: string
  acknowledgedAt?: string     // undefined = not yet signed
}

interface ToolboxTalk {
  id: string
  talkNumber: string
  jobId: string
  jobNumber: string
  conductedById: string
  conductedByName: string
  date: string
  topic: string
  summary: string
  signoffRequired: boolean
  status: 'DRAFT' | 'COMPLETED'
  attendees: ToolboxAttendee[]
}

// ─────────────────────────────────────────────────────────────────
// LOCAL MOCK DATA — extended talks with per-worker acknowledgements
// ─────────────────────────────────────────────────────────────────

const INITIAL_TALKS: ToolboxTalk[] = [
  {
    id: 'tb1', talkNumber: 'TBT-2026-003',
    jobId: 'j1', jobNumber: 'TP-2026-001',
    conductedById: 'e8', conductedByName: 'Sarah Kim',
    date: '2026-06-16', signoffRequired: true, status: 'COMPLETED',
    topic: 'Traffic management review and heat / fatigue awareness',
    summary: 'Reviewed the current TMP for Kessels Rd. Reminded crew of mandatory rest requirements under QLD fatigue law. Covered correct PPE use in heat — hydration, shade access, buddy system. Discussed end-of-day site security checklist — all open pits must be covered and barricaded before crew departs.',
    attendees: [
      { id: 'ta1', workerId: 'e1', workerName: 'Tony Rivera',   workerType: 'EMPLOYEE',      acknowledgedAt: '2026-06-16T07:05:00Z' },
      { id: 'ta2', workerId: 'e2', workerName: 'Lisa Grant',    workerType: 'EMPLOYEE',      acknowledgedAt: '2026-06-16T06:58:00Z' },
      { id: 'ta3', workerId: 'e6', workerName: 'Dave Fielding', workerType: 'EMPLOYEE',      acknowledgedAt: '2026-06-16T07:10:00Z' },
    ],
  },
  {
    id: 'tb2', talkNumber: 'TBT-2026-002',
    jobId: 'j4', jobNumber: 'TP-2026-004',
    conductedById: 'e3', conductedByName: 'Sam Nguyen',
    date: '2026-06-15', signoffRequired: true, status: 'COMPLETED',
    topic: 'HV safety — near-miss debrief and undocumented underground services',
    summary: 'Debriefed the crew on INC-2026-001 — excavator strike on unmarked LV conduit. Key lesson: BYDA plans are not always complete. All excavation within 1m of any service must now be by hand. Confirmed the updated SWMS-2026-004 Rev 3 hand-dig zone (500mm increased to 500mm from any marker). HV authority holder on site at all times during switching.',
    attendees: [
      { id: 'ta4', workerId: 'e3', workerName: 'Sam Nguyen', workerType: 'EMPLOYEE', acknowledgedAt: '2026-06-15T07:15:00Z' },
    ],
  },
  {
    id: 'tb3', talkNumber: 'TBT-2026-001',
    jobId: 'j1', jobNumber: 'TP-2026-001',
    conductedById: 'e8', conductedByName: 'Sarah Kim',
    date: '2026-06-09', signoffRequired: true, status: 'COMPLETED',
    topic: 'Site establishment and emergency procedures',
    summary: 'Site emergency plan reviewed with full crew on first day. Muster point confirmed — north gate compound. First aiders identified: Tony Rivera (0412 345 678). Emergency contacts posted at compound and in all site vehicles. Nearest hospital: Princess Alexandra, 2.3km via Ipswich Rd. All workers confirmed they read and understood the site induction.',
    attendees: [
      { id: 'ta5', workerId: 'e1', workerName: 'Tony Rivera',   workerType: 'EMPLOYEE', acknowledgedAt: '2026-06-09T07:02:00Z' },
      { id: 'ta6', workerId: 'e2', workerName: 'Lisa Grant',    workerType: 'EMPLOYEE', acknowledgedAt: '2026-06-09T06:55:00Z' },
      { id: 'ta7', workerId: 'e6', workerName: 'Dave Fielding', workerType: 'EMPLOYEE', acknowledgedAt: '2026-06-09T07:08:00Z' },
      { id: 'ta8', workerId: 'e8', workerName: 'Sarah Kim',     workerType: 'EMPLOYEE', acknowledgedAt: '2026-06-09T06:50:00Z' },
    ],
  },
  {
    id: 'tb4', talkNumber: 'TBT-2026-004',
    jobId: 'j1', jobNumber: 'TP-2026-001',
    conductedById: 'e8', conductedByName: 'Sarah Kim',
    date: '2026-06-20', signoffRequired: true, status: 'COMPLETED',
    topic: 'Excavation safety and open pit barricading',
    summary: 'Following the INS-2026-001 pre-start inspection finding (Pit 5 unbarricaded), reminded all crew of the end-of-day checklist. Every open pit must have steel plate cover AND barricade tape before leaving site. No exceptions. Photographs to be taken of all covered pits and submitted with site diary.',
    attendees: [
      { id: 'ta9',  workerId: 'e1', workerName: 'Tony Rivera',   workerType: 'EMPLOYEE', acknowledgedAt: '2026-06-20T06:35:00Z' },
      { id: 'ta10', workerId: 'e6', workerName: 'Dave Fielding', workerType: 'EMPLOYEE', acknowledgedAt: '2026-06-20T06:40:00Z' },
      { id: 'ta11', workerId: 'ext_001', workerName: 'Brad Thomson', workerType: 'SUBCONTRACTOR', company: 'Thomson Electrical', acknowledgedAt: undefined },
    ],
  },
]

// ─────────────────────────────────────────────────────────────────
// TOPIC LIBRARY — pre-built topics for civil electrical work
// ─────────────────────────────────────────────────────────────────

const TOPIC_LIBRARY: { category: string; topics: string[] }[] = [
  {
    category: 'Electrical Safety',
    topics: [
      'Electrical isolation procedures and LOTO (Lockout/Tagout)',
      'Test-before-touch — always test equipment is de-energised',
      'Working near overhead power lines — exclusion zones',
      'HV cable jointing and termination safety',
      'Arc flash awareness and PPE requirements',
      'Underground cable installation — safe practices',
    ],
  },
  {
    category: 'Excavation & Civil',
    topics: [
      'Underground services — Dial Before You Dig procedure',
      'Excavation safety and trench stability',
      'Hand-dig exclusion zones around services',
      'Open pit barricading and end-of-day site security',
      'Dewatering management and ground conditions',
    ],
  },
  {
    category: 'Traffic Management',
    topics: [
      'Working in live traffic — personal safety',
      'Traffic Management Plan review and changes',
      'Traffic controller handover and communication procedures',
      'Speed zone compliance and pedestrian safety',
    ],
  },
  {
    category: 'Confined Spaces',
    topics: [
      'Confined space entry procedure and atmospheric testing',
      'Standby person responsibilities',
      'Emergency rescue plan for cable pits and vaults',
    ],
  },
  {
    category: 'Health & Wellbeing',
    topics: [
      'Heat stress and fatigue management — QLD summer',
      'Manual handling of heavy cable drums and reels',
      'Hydration, shade, and rest break requirements',
      'Mental health and asking for help on site',
    ],
  },
  {
    category: 'WHS General',
    topics: [
      'Near miss reporting — why it matters and how to do it',
      'Emergency procedures and site evacuation plan',
      'PPE — correct selection, fit, and use',
      'Incident reporting obligations under the WHS Act',
      'Environmental controls — spill management and dewatering',
    ],
  },
]

// ─────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────

const SEVERITY_META: Record<NotificationSeverity, { label: string; bg: string; text: string; border: string; icon: any }> = {
  INFO:    { label: 'Info',    bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',   icon: Bell },
  WARNING: { label: 'Warning', bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  icon: AlertTriangle },
  URGENT:  { label: 'Urgent',  bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    icon: Megaphone },
}

const AUDIENCE_META: Record<NotificationAudience, { label: string; icon: any }> = {
  ALL_SITE:         { label: 'All site workers',   icon: Users },
  TRADE:            { label: 'Specific trade',     icon: Users },
  SPECIFIC_WORKERS: { label: 'Specific workers',   icon: Users },
}

// ─────────────────────────────────────────────────────────────────
// SMALL COMPONENTS
// ─────────────────────────────────────────────────────────────────

function AckBadge({ signed, total }: { signed: number; total: number }) {
  const pct  = total > 0 ? Math.round((signed / total) * 100) : 0
  const full = signed === total
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full font-medium
      ${full ? 'bg-green-100 text-green-700' : signed > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
      {full ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
      {signed}/{total} signed
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────
// TOPIC LIBRARY MODAL
// ─────────────────────────────────────────────────────────────────

function TopicLibraryModal({ onSelect, onClose }: { onSelect: (topic: string) => void; onClose: () => void }) {
  const [search, setSearch] = useState('')
  const filtered = useMemo(() =>
    TOPIC_LIBRARY.map(cat => ({
      ...cat,
      topics: cat.topics.filter(t => !search || t.toLowerCase().includes(search.toLowerCase())),
    })).filter(cat => cat.topics.length > 0),
    [search]
  )

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-semibold text-slate-900">Topic library</h3>
            <p className="text-xs text-slate-400 mt-0.5">Pre-built topics for civil electrical safety</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="px-4 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search topics..." className="input pl-9 w-full text-sm" autoFocus />
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          {filtered.map(cat => (
            <div key={cat.category}>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{cat.category}</p>
              <div className="space-y-1.5">
                {cat.topics.map(topic => (
                  <button key={topic} onClick={() => { onSelect(topic); onClose() }}
                    className="w-full text-left text-sm text-slate-700 px-3 py-2.5 rounded-lg hover:bg-indigo-50 hover:text-indigo-700 transition-colors border border-transparent hover:border-indigo-200">
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// CONDUCT TALK MODAL
// ─────────────────────────────────────────────────────────────────

function ConductTalkModal({ onClose, onSubmit }: {
  onClose: () => void
  onSubmit: (talk: ToolboxTalk) => void
}) {
  const { user } = useAuthStore()
  const [form, setForm] = useState({
    jobId:           MOCK_JOBS[0]?.id ?? '',
    topic:           '',
    summary:         '',
    signoffRequired: true,
    selectedWorkers: [] as string[],
  })
  const [showLibrary, setShowLibrary] = useState(false)
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))

  const toggleWorker = (id: string) =>
    setForm(f => ({
      ...f,
      selectedWorkers: f.selectedWorkers.includes(id)
        ? f.selectedWorkers.filter(w => w !== id)
        : [...f.selectedWorkers, id],
    }))

  const job = MOCK_JOBS.find(j => j.id === form.jobId)
  const canSubmit = form.topic.trim() && form.summary.trim() && form.selectedWorkers.length > 0

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    const num  = String(INITIAL_TALKS.length + 1).padStart(3, '0')
    const now  = new Date().toISOString()
    onSubmit({
      id:               `tb_${Date.now()}`,
      talkNumber:       `TBT-2026-${num}`,
      jobId:            form.jobId,
      jobNumber:        job?.jobNumber ?? form.jobId,
      conductedById:    user?.id ?? 'u1',
      conductedByName:  user?.fullName ?? 'Current User',
      date:             now.slice(0, 10),
      topic:            form.topic,
      summary:          form.summary,
      signoffRequired:  form.signoffRequired,
      status:           'COMPLETED',
      attendees: form.selectedWorkers.map((wId, i) => {
        const emp = MOCK_EMPLOYEES.find(e => e.id === wId)
        return {
          id:           `ta_new_${i}`,
          workerId:     wId,
          workerName:   emp?.fullName ?? wId,
          workerType:   'EMPLOYEE' as const,
          acknowledgedAt: now,     // face-to-face — immediate sign-off
        }
      }),
    })
    onClose()
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="font-semibold text-slate-900">Conduct toolbox talk</h2>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Job */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Project / job *</label>
              <select value={form.jobId} onChange={e => set('jobId', e.target.value)} className="input w-full text-sm">
                {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
              </select>
            </div>

            {/* Topic */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-slate-600">Topic *</label>
                <button type="button" onClick={() => setShowLibrary(true)}
                  className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                  <BookOpen className="w-3 h-3" /> Topic library
                </button>
              </div>
              <input
                value={form.topic}
                onChange={e => set('topic', e.target.value)}
                placeholder="e.g. Excavation safety and trench stability"
                className="input w-full text-sm"
                required
              />
            </div>

            {/* Summary */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Summary of what was discussed *
              </label>
              <textarea
                value={form.summary}
                onChange={e => set('summary', e.target.value)}
                placeholder="Describe the key points covered, any decisions made, and follow-up actions..."
                className="input w-full resize-none h-28 text-sm"
                required
              />
            </div>

            {/* Attendees */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">
                Attendees * <span className="text-slate-400 font-normal">({form.selectedWorkers.length} selected)</span>
              </label>
              <div className="border border-slate-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                {MOCK_EMPLOYEES.map(emp => {
                  const selected = form.selectedWorkers.includes(emp.id)
                  return (
                    <label key={emp.id}
                      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors border-b border-slate-100 last:border-0
                        ${selected ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleWorker(emp.id)}
                        className="w-4 h-4 accent-indigo-600"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{emp.fullName}</p>
                        <p className="text-xs text-slate-400">{emp.trade} · {emp.currentJobNumber ?? 'unassigned'}</p>
                      </div>
                      {selected && <CheckCircle className="w-4 h-4 text-indigo-500 flex-shrink-0" />}
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Signoff toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                onClick={() => set('signoffRequired', !form.signoffRequired)}
                className={`relative w-10 h-5 rounded-full transition-colors ${form.signoffRequired ? 'bg-indigo-600' : 'bg-slate-200'}`}>
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.signoffRequired ? 'translate-x-5' : ''}`} />
              </div>
              <span className="text-sm text-slate-700">Require digital sign-off from each attendee</span>
            </label>

            {form.selectedWorkers.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-xs text-green-700">
                <strong>Face-to-face talk:</strong> All {form.selectedWorkers.length} selected attendees will be marked as signed off immediately. Digital sign-off links will be sent to remote workers in production.
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose}
                className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button type="submit" disabled={!canSubmit}
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-40 transition-colors">
                Record talk & sign off
              </button>
            </div>
          </form>
        </div>
      </div>

      {showLibrary && (
        <TopicLibraryModal
          onSelect={topic => set('topic', topic)}
          onClose={() => setShowLibrary(false)}
        />
      )}
    </>
  )
}

// ─────────────────────────────────────────────────────────────────
// TALK DETAIL MODAL
// ─────────────────────────────────────────────────────────────────

// Digital sign-off modal for a single attendee.
// Pops when a worker taps "Sign off now" in the detail view.
function AttendeeSignOffModal({
  attendee, talkNumber, onConfirm, onClose,
}: {
  attendee: ToolboxAttendee
  talkNumber: string
  onConfirm: () => void
  onClose: () => void
}) {
  const [declared, setDeclared] = useState(false)
  const now = new Date().toLocaleString('en-AU', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-indigo-50">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-indigo-600" />Sign off — {talkNumber}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{attendee.workerName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 text-xs text-indigo-800 leading-relaxed">
            <p className="font-semibold mb-1">Worker declaration:</p>
            <p>I confirm that I attended this toolbox talk and that I have understood the safety matters discussed, including the hazards, controls, and any changes to work procedures described. I understand I must not commence the relevant work activity until I have received and understood this briefing.</p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={declared} onChange={e => setDeclared(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-indigo-600 flex-shrink-0" />
            <span className="text-xs text-gray-700">
              I, <strong>{attendee.workerName}</strong>, confirm I attended this toolbox talk and understood the content.<br />
              <strong className="text-gray-900">Time recorded: {now}</strong>
            </span>
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button onClick={() => declared && onConfirm()} disabled={!declared}
              className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
              <CheckCircle className="w-3.5 h-3.5" />Confirm sign-off
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Toolbox Talk Attendance Record — printable field document
function printToolboxTalk(talk: ToolboxTalk, currentAttendees: ToolboxAttendee[]) {
  const now    = new Date().toLocaleDateString('en-AU', { day:'numeric', month:'long', year:'numeric' })
  const fmtDT  = (iso?: string) => iso ? new Date(iso).toLocaleString('en-AU', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'
  const fmtD   = (iso: string) => new Date(iso).toLocaleDateString('en-AU', { day:'numeric', month:'long', year:'numeric' })

  const signonRows = currentAttendees.map((a, i) => `
    <tr style="background:${i%2===0?'#fff':'#fafafa'}">
      <td style="padding:7px 10px;font-size:9.5pt;font-weight:600">${a.workerName}</td>
      <td style="padding:7px 8px;font-size:9pt;color:#555">${a.workerType === 'SUBCONTRACTOR' ? `Subcontractor${a.company ? ' · ' + a.company : ''}` : 'MCB Civil Electrical'}</td>
      <td style="padding:7px 8px;font-size:9pt;color:${a.acknowledgedAt?'#166534':'#92400e'};font-weight:bold;text-align:center">${a.acknowledgedAt ? '✓ ' + fmtDT(a.acknowledgedAt) : 'Pending'}</td>
      <td style="padding:7px 8px;text-align:center"><div style="border-bottom:1px solid #ccc;margin:12px 8px 0"></div></td>
    </tr>`).join('')

  const blankRows = Array.from({ length: Math.max(4 - currentAttendees.length, 2) }, () =>
    `<tr style="height:42px"><td style="border-bottom:1px solid #e5e7eb"></td><td style="border-bottom:1px solid #e5e7eb"></td><td style="border-bottom:1px solid #e5e7eb"></td><td style="border-bottom:1px solid #e5e7eb"><div style="border-bottom:1px solid #ccc;margin:4px 8px"></div></td></tr>`
  ).join('')

  const signed   = currentAttendees.filter(a => a.acknowledgedAt).length
  const unsigned = currentAttendees.length - signed

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Toolbox Talk — ${talk.talkNumber}</title>
<style>*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:16mm}
h1{font-size:19pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:10.5pt;font-weight:700;color:#1B3A6B;border-bottom:2px solid #1B3A6B;padding-bottom:3px;margin:14px 0 8px}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:9px;margin-bottom:12px;display:flex;justify-content:space-between;align-items:flex-end}
.meta{display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;margin-bottom:12px}
.mb{border:1px solid #e5e7eb;border-radius:4px;padding:6px 10px}
.mb .l{font-size:7.5pt;color:#9ca3af;margin-bottom:1px}.mb .v{font-weight:600;font-size:9.5pt}
.summary-box{background:#f8fafc;border-left:4px solid #1B3A6B;padding:10px 14px;margin-bottom:12px;line-height:1.7;font-size:9.5pt}
.decl-box{background:#fef3c7;border:1.5px solid #d97706;border-radius:5px;padding:10px 14px;margin-bottom:12px;font-size:9.5pt;line-height:1.7}
table{width:100%;border-collapse:collapse;margin-bottom:10px}
th{background:#1B3A6B;color:#fff;padding:6px 10px;text-align:left;font-size:9.5pt}
.kpi{display:flex;gap:10px;margin-bottom:12px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:4px;padding:7px;text-align:center}
.kpi-v{font-size:16pt;font-weight:900}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:14px;border-top:1px solid #eee;padding-top:6px}
@page{size:A4;margin:12mm}@media print{body{margin:0}}</style></head><body>

<div class="hdr">
  <div><h1>MCB Civil Electrical Pty Ltd</h1><div style="font-size:9pt;color:#888">ABN 44 128 993 087 · 14 Commerce Dr, Salisbury QLD 4107</div></div>
  <div style="text-align:right"><div style="font-size:13pt;font-weight:900;color:#1B3A6B">TOOLBOX TALK RECORD</div><div style="font-size:8.5pt;color:#888">WHS Act 2011 (Qld) · Attendance &amp; sign-off record</div></div>
</div>

<div class="meta">
  <div class="mb"><div class="l">Talk reference</div><div class="v">${talk.talkNumber}</div></div>
  <div class="mb"><div class="l">Date conducted</div><div class="v">${fmtD(talk.date)}</div></div>
  <div class="mb"><div class="l">Job / project</div><div class="v">${talk.jobNumber}</div></div>
  <div class="mb" style="grid-column:span 2"><div class="l">Topic</div><div class="v" style="font-size:10.5pt">${talk.topic}</div></div>
  <div class="mb"><div class="l">Conducted by</div><div class="v">${talk.conductedByName}</div></div>
</div>

<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:#1B3A6B">${currentAttendees.length}</div><div style="font-size:9pt;color:#6b7280">Attendees</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#166534">${signed}</div><div style="font-size:9pt;color:#6b7280">Signed off</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:${unsigned>0?'#d97706':'#166534'}">${unsigned}</div><div style="font-size:9pt;color:#6b7280">Pending</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#1B3A6B;font-size:13pt">${now}</div><div style="font-size:9pt;color:#6b7280">Printed</div></div>
</div>

<h2>Summary of discussion</h2>
<div class="summary-box">${talk.summary}</div>

<h2>Worker attendance declaration &amp; sign-off</h2>
<div class="decl-box">Workers listed below confirm that they were present for this toolbox talk and have understood the safety matters discussed. By signing, each worker confirms they will apply the controls and safe work methods described to their work activities until further notice.</div>

<table><thead><tr>
  <th style="width:30%">Full name</th>
  <th style="width:30%">Company / employer</th>
  <th style="width:25%;text-align:center">Digital sign-off time</th>
  <th style="width:15%;text-align:center">Signature</th>
</tr></thead>
<tbody>${signonRows}${blankRows}</tbody></table>

<div class="footer">MCB Civil Electrical Pty Ltd · ${talk.talkNumber} · ${talk.topic} · Printed ${now}<br>Retain this record with the project WHS register for a minimum of 5 years as required under the Work Health and Safety Act 2011 (Qld).</div>
</body></html>`

  const w = window.open('', '_blank', 'width=1020,height=820')
  if (!w) { alert('Pop-up blocked — allow pop-ups to print Toolbox Talk records.'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 400)
}

function TalkDetailModal({ talk: initialTalk, onClose, onRemind, onAttendeeSignOff }: {
  talk: ToolboxTalk
  onClose: () => void
  onRemind: (talkId: string, workerId: string) => void
  onAttendeeSignOff?: (attendeeId: string) => void
}) {
  // Local copy of attendees so sign-offs update the UI immediately without re-opening
  const [attendees, setAttendees] = useState<ToolboxAttendee[]>(initialTalk.attendees)
  const [signingAtt, setSigningAtt] = useState<ToolboxAttendee | null>(null)

  const handleSignOff = (att: ToolboxAttendee) => {
    const signedAt = new Date().toISOString()
    setAttendees(prev => prev.map(a => a.id === att.id ? { ...a, acknowledgedAt: signedAt } : a))
    onAttendeeSignOff?.(att.id)
    setSigningAtt(null)
  }

  const signed   = attendees.filter(a => a.acknowledgedAt).length
  const unsigned = attendees.filter(a => !a.acknowledgedAt)

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-bold text-slate-900 text-sm">{initialTalk.talkNumber}</span>
              <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{initialTalk.jobNumber}</span>
              <span className="text-xs text-slate-400">{formatDate(initialTalk.date)}</span>
            </div>
            <p className="text-sm font-medium text-slate-700 leading-snug">{initialTalk.topic}</p>
            <p className="text-xs text-slate-400 mt-0.5">Conducted by {initialTalk.conductedByName}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => printToolboxTalk(initialTalk, attendees)}
              className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-2.5 py-1.5 rounded-lg transition-colors"
            >
              <Download className="w-3.5 h-3.5" />Print
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          {/* Summary */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Summary</p>
            <p className="text-sm text-slate-600 leading-relaxed">{initialTalk.summary}</p>
          </div>

          {/* Attendance */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Attendance &amp; sign-off
              </p>
              <AckBadge signed={signed} total={attendees.length} />
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden">
              {attendees.map((att, i) => (
                <div key={att.id}
                  className={`flex items-center justify-between px-4 py-3 border-b border-slate-100 last:border-0
                    ${!att.acknowledgedAt ? 'bg-amber-50/50' : ''}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
                      ${att.acknowledgedAt ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {att.workerName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{att.workerName}</p>
                      <p className="text-xs text-slate-400">
                        {att.workerType === 'SUBCONTRACTOR' && att.company ? att.company : 'MCB Civil Electrical'}
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    {att.acknowledgedAt ? (
                      <span className="flex items-center gap-1 text-xs text-green-700">
                        <CheckCircle className="w-3.5 h-3.5" />
                        {new Date(att.acknowledgedAt).toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit'})}
                      </span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setSigningAtt(att)}
                          className="flex items-center gap-1 text-xs text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-lg border border-indigo-200 hover:bg-indigo-100 transition-colors font-medium">
                          <CheckCircle className="w-3 h-3" /> Sign off now
                        </button>
                        <button onClick={() => onRemind(initialTalk.id, att.workerId)}
                          className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-lg border border-amber-200 hover:bg-amber-100 transition-colors">
                          <Smartphone className="w-3 h-3" /> Remind
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {unsigned.length > 0 && (
              <div className="mt-2 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                {unsigned.length} worker{unsigned.length !== 1 ? 's' : ''} haven't signed off yet.
                <button onClick={() => unsigned.forEach(a => onRemind(initialTalk.id, a.workerId))}
                  className="ml-auto underline underline-offset-2 hover:text-amber-900 whitespace-nowrap">
                  Remind all
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-slate-50 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 border border-slate-200 bg-white rounded-lg text-sm text-slate-600 hover:bg-slate-50">Close</button>
          <button onClick={() => generateToolboxRecordPDF({ ...initialTalk, attendees })} className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
            Print record PDF
          </button>
        </div>
      </div>
    </div>

    {/* Digital sign-off modal */}
    {signingAtt && (
      <AttendeeSignOffModal
        attendee={signingAtt}
        talkNumber={initialTalk.talkNumber}
        onConfirm={() => handleSignOff(signingAtt)}
        onClose={() => setSigningAtt(null)}
      />
    )}
  </>
  )
}

// ─────────────────────────────────────────────────────────────────
// SEND NOTIFICATION MODAL
// ─────────────────────────────────────────────────────────────────

function SendNotificationModal({ onClose, onSubmit }: {
  onClose: () => void
  onSubmit: (n: WHSNotification) => void
}) {
  const { user } = useAuthStore()
  const [form, setForm] = useState({
    severity: 'INFO' as NotificationSeverity,
    audience: 'ALL_SITE' as NotificationAudience,
    jobId:    '',
    title:    '',
    body:     '',
    sendSMS:  false,
  })
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))
  const canSubmit = form.title.trim() && form.body.trim()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    const job = MOCK_JOBS.find(j => j.id === form.jobId)
    const num = String(MOCK_WHS_NOTIFICATIONS.length + 1).padStart(3, '0')
    onSubmit({
      id:                 `notif_${Date.now()}`,
      notificationNumber: `NOTIF-2026-${num}`,
      jobId:              form.jobId || undefined,
      jobNumber:          job?.jobNumber,
      severity:           form.severity,
      audience:           form.audience,
      title:              form.title,
      body:               form.body,
      sentById:           user?.id ?? 'u1',
      sentByName:         user?.fullName ?? 'Current User',
      sentAt:             new Date().toISOString(),
      deliveredCount:     form.audience === 'ALL_SITE' ? 4 : 1,
      acknowledgedCount:  0,
      smsDispatched:      form.sendSMS,
    })
    onClose()
  }

  const sev = SEVERITY_META[form.severity]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-slate-900">Send WHS notification</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Severity */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Severity *</label>
            <div className="grid grid-cols-3 gap-2">
              {(['INFO', 'WARNING', 'URGENT'] as NotificationSeverity[]).map(s => {
                const m  = SEVERITY_META[s]
                const Icon = m.icon
                return (
                  <button key={s} type="button" onClick={() => set('severity', s)}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-colors ${
                      form.severity === s ? `border-current ${m.bg} ${m.text}` : 'border-slate-200 text-slate-400 hover:border-slate-300'}`}>
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-semibold">{m.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Audience + Job */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Send to *</label>
              <select value={form.audience} onChange={e => set('audience', e.target.value)} className="input w-full text-sm">
                <option value="ALL_SITE">All site workers</option>
                <option value="TRADE">Specific trade</option>
                <option value="SPECIFIC_WORKERS">Specific workers</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Project (optional)</label>
              <select value={form.jobId} onChange={e => set('jobId', e.target.value)} className="input w-full text-sm">
                <option value="">All projects</option>
                {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
              </select>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)}
              placeholder={form.severity === 'URGENT' ? 'e.g. IMMEDIATE — Evacuate site now' : 'e.g. Toolbox talk reminder — Monday 07:00'}
              className="input w-full text-sm" required />
          </div>

          {/* Body */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Message *</label>
            <textarea value={form.body} onChange={e => set('body', e.target.value)}
              placeholder="Enter the full message workers will receive..."
              className="input w-full resize-none h-28 text-sm" required />
          </div>

          {/* SMS toggle */}
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl bg-slate-50 border border-slate-200">
            <div onClick={() => set('sendSMS', !form.sendSMS)}
              className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${form.sendSMS ? 'bg-indigo-600' : 'bg-slate-300'}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.sendSMS ? 'translate-x-5' : ''}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">Also send by SMS</p>
              <p className="text-xs text-slate-400">Workers receive a text message in addition to the in-app notification</p>
            </div>
            <Smartphone className={`w-4 h-4 flex-shrink-0 ${form.sendSMS ? 'text-indigo-600' : 'text-slate-300'}`} />
          </label>

          {/* Preview strip */}
          {form.title && (
            <div className={`rounded-xl p-4 border ${sev.border} ${sev.bg}`}>
              <p className="text-xs font-semibold text-slate-500 mb-2">Preview</p>
              <div className="flex items-center gap-2 mb-1">
                <sev.icon className={`w-4 h-4 ${sev.text}`} />
                <p className={`text-sm font-bold ${sev.text}`}>{form.title}</p>
              </div>
              {form.body && <p className="text-xs text-slate-600 line-clamp-2">{form.body}</p>}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={!canSubmit}
              className={`flex-1 py-2.5 text-white rounded-lg text-sm font-medium disabled:opacity-40 transition-colors flex items-center justify-center gap-2
                ${form.severity === 'URGENT' ? 'bg-red-600 hover:bg-red-700' : form.severity === 'WARNING' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
              <Send className="w-4 h-4" />
              {form.sendSMS ? 'Send notification + SMS' : 'Send notification'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────



// ─────────────────────────────────────────────────────────────────
// PHASE 2 — TOOLBOX TALK RECORD PDF
// Formatted attendance sheet for WHSO records and client audit.
// Required as evidence of worker engagement and WHS briefings.
// ─────────────────────────────────────────────────────────────────
function generateToolboxRecordPDF(talk: ToolboxTalk) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const date   = new Date(talk.date).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

  const attendeeRows = (talk.attendees ?? []).map((a: any, i: number) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:7px 10px">${a.workerName}</td>
      <td style="padding:7px 10px">${(a.workerType ?? '').replace(/_/g,' ')}</td>
      <td style="padding:7px 10px;text-align:center">${a.acknowledgedAt ? new Date(a.acknowledgedAt).toLocaleTimeString('en-AU', { hour:'2-digit', minute:'2-digit' }) : '—'}</td>
      <td style="padding:7px 10px;text-align:center">${a.acknowledgedAt ? '✓ Digital' : '—'}</td>
      <td style="padding:7px 10px;min-width:90px;border-bottom:1px solid #bbb">&nbsp;</td>
    </tr>`).join('')

  // Empty rows for paper sign-on
  const blankRows = [1,2,3,4,5].map((_, i) =>
    `<tr style="background:${(talk.attendees.length + i)%2===0?'#fff':'#f9fafb'}"><td style="padding:22px 10px;border-bottom:1px solid #e5e7eb"></td><td style="border-bottom:1px solid #e5e7eb"></td><td style="border-bottom:1px solid #e5e7eb;text-align:center"></td><td style="border-bottom:1px solid #e5e7eb;text-align:center"></td><td style="border-bottom:1px solid #bbb"></td></tr>`
  ).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Toolbox Talk Record — ${talk.talkNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:16mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:11pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:14px 0 8px}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
.info table{width:100%;border-collapse:collapse;margin-bottom:10px}
.info td{padding:3px 6px;vertical-align:top}.info td:first-child{width:34%;font-weight:bold;color:#555}
table.att{width:100%;border-collapse:collapse;font-size:10pt;margin-bottom:12px}
th{background:#1B3A6B;color:#fff;padding:6px 10px;text-align:left}
.notes-box{border:1px solid #ccc;border-radius:4px;padding:10px 14px;background:#fafafa;margin-bottom:12px;min-height:60px;font-size:10.5pt;white-space:pre-line}
.sig-row{display:flex;gap:30px;margin-top:14px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:30px;margin-bottom:4px}
.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:20px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:12mm}}
</style></head><body>

<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901 · 14 Commerce Dr, Salisbury QLD 4107</div></div>
  <div style="text-align:right">
    <div style="font-size:17pt;font-weight:900;color:#1B3A6B">TOOLBOX TALK RECORD</div>
    <div style="font-family:monospace;font-size:12pt;font-weight:bold;color:#1B3A6B">${talk.talkNumber}</div>
    <div style="font-size:9pt;color:#888">Generated: ${now}</div>
  </div>
</div>

<div class="info"><table>
  <tr><td>Date</td><td><strong>${date}</strong></td>
      <td>Job / project</td><td><strong>${talk.jobNumber}</strong></td></tr>
  <tr><td>Conducted by</td><td>${talk.conductedByName}</td>
      <td>Status</td><td>${(talk.status ?? '').replace(/_/g,' ')}</td></tr>
  <tr><td>Sign-off required</td><td>${talk.signoffRequired ? 'Yes' : 'No'}</td>
      <td>Attendees recorded</td><td>${(talk.attendees ?? []).length}</td></tr>
</table></div>

<h2>Topic</h2>
<p style="font-size:12pt;font-weight:bold;color:#1B3A6B;margin:0 0 10px">${talk.topic}</p>

<h2>Talk summary / content covered</h2>
<div class="notes-box">${talk.summary ?? 'No summary recorded.'}</div>

<h2>Attendance register</h2>
<p style="font-size:9.5pt;color:#555;margin-bottom:6px">All workers attending the toolbox talk must sign or acknowledge digitally. Failure to sign means the worker has NOT been briefed and may not commence the relevant work activity.</p>
<table class="att">
  <thead><tr>
    <th>Name</th><th>Company / type</th><th style="text-align:center">Time</th>
    <th style="text-align:center">Digital ack.</th><th>Signature</th>
  </tr></thead>
  <tbody>
    ${attendeeRows}
    ${blankRows}
  </tbody>
</table>

<h2>Declaration by facilitator</h2>
<p style="font-size:9.5pt;color:#555">I confirm that the toolbox talk described above was conducted on the date and with the workers recorded, that the content was covered in sufficient depth for all workers to understand, and that the opportunity was provided for workers to ask questions.</p>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Conducted by — name &amp; date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">WHSO / supervisor witness — name &amp; date</div></div>
</div>

<div class="footer">MCB Civil Electrical · Toolbox Talk Record · ${talk.talkNumber} · ${talk.jobNumber} · ${date} · Generated ${now}</div>
</body></html>`

  const win = window.open('', '_blank', 'width=960,height=800')
  if (!win) { alert('Pop-up blocked — allow pop-ups to print record'); return }
  win.document.write(html); win.document.close()
  setTimeout(() => { win.focus(); win.print() }, 350)
}
export function Toolbox() {
  const { isTier } = useAuthStore()

  const [talks, setTalks]               = useState<ToolboxTalk[]>(INITIAL_TALKS)
  const [apiSeeded, setApiSeeded]       = useState(false)
  const [notifications, setNotifications] = useState<WHSNotification[]>(MOCK_WHS_NOTIFICATIONS)

  // Live toolbox talks from Neon — seeded into state on first load
  const { data: apiTalks = [] } = useQuery({
    queryKey: ['toolbox-talks'],
    queryFn:  () => fetchToolboxTalks(),
    staleTime: 60_000,
  })

  useEffect(() => {
    if (apiTalks.length > 0 && !apiSeeded) {
      const apiIds = new Set(apiTalks.map((t: any) => t.id))
      setTalks([
        ...(apiTalks as unknown as ToolboxTalk[]),
        ...INITIAL_TALKS.filter(t => !apiIds.has(t.id)),
      ])
      setApiSeeded(true)
    }
  }, [apiTalks, apiSeeded])
  const [tab, setTab]                   = useState<'talks' | 'notifications'>('talks')
  const [search, setSearch]             = useState('')
  const [jobFilter, setJobFilter]       = useState('ALL')
  const [selectedTalk, setSelectedTalk] = useState<ToolboxTalk | null>(null)
  const [showConduct, setShowConduct]   = useState(false)
  const [showNotify, setShowNotify]     = useState(false)
  const [remindedWorkers, setRemindedWorkers] = useState<Set<string>>(new Set())

  const canConduct = isTier('B_PC') || isTier('C_WHSO')   // WHS Officer and PC/PM/Admin can conduct talks and send notifications

  const queryClient = useQueryClient()

  // Persist conducted talk to Neon via API
  const saveTalkMutation = useMutation({
    mutationFn: async (talk: ToolboxTalk) => {
      const res = await fetch(
        (import.meta.env.VITE_API_URL ?? 'http://localhost:8787') + '/api/whs/toolbox',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jobId:        talk.jobId,
            topic:        talk.topic,
            notes:        talk.summary,
            conductedAt:  new Date(talk.date).toISOString(),
          }),
        }
      )
      if (!res.ok) console.warn('[Toolbox] API save failed — talk saved locally only')
      return res.ok ? res.json() : null
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['toolbox-talks'] }),
  })

  // ── Derived stats ──────────────────────────────────────────────
  const stats = useMemo(() => {
    const thisMonth = new Date().toISOString().slice(0, 7)
    const allAttendees = talks.flatMap(t => t.attendees)
    const unsigned = notifications.filter(n => n.acknowledgedCount < n.deliveredCount).length
    return {
      talksThisMonth: talks.filter(t => t.date.startsWith(thisMonth)).length,
      totalAttendance: allAttendees.length,
      pendingSignoff:  allAttendees.filter(a => !a.acknowledgedAt).length,
      unacknowledgedNotifs: unsigned,
    }
  }, [talks, notifications])

  // ── Filtered talks ─────────────────────────────────────────────
  const filteredTalks = useMemo(() =>
    talks.filter(t => {
      const s = search.toLowerCase()
      const matchSearch = !s || t.topic.toLowerCase().includes(s) || t.jobNumber.toLowerCase().includes(s) || t.conductedByName.toLowerCase().includes(s)
      const matchJob    = jobFilter === 'ALL' || t.jobId === jobFilter
      return matchSearch && matchJob
    }).sort((a, b) => b.date.localeCompare(a.date)),
    [talks, search, jobFilter]
  )

  // ── Handlers ──────────────────────────────────────────────────
  const handleNewTalk = (talk: ToolboxTalk) => {
    setTalks(prev => [talk, ...prev])
    saveTalkMutation.mutate(talk)   // persist to Neon; falls back to local if API unreachable
  }

  const handleRemind = (talkId: string, workerId: string) => {
    const key = `${talkId}-${workerId}`
    setRemindedWorkers(prev => new Set([...prev, key]))
    // In production: sends SMS to worker
  }

  const handleNewNotification = (n: WHSNotification) =>
    setNotifications(prev => [n, ...prev])

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Toolbox Talks</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Safety meetings, attendee sign-off, and urgent site notifications
          </p>
        </div>
        {canConduct && (
          <div className="flex gap-2">
            <button onClick={() => setShowNotify(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors">
              <Megaphone className="w-4 h-4" /> Notify workers
            </button>
            <button onClick={() => setShowConduct(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors">
              <Plus className="w-4 h-4" /> Conduct talk
            </button>
          </div>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Talks this month',    value: stats.talksThisMonth,       color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Total attendances',   value: stats.totalAttendance,      color: 'text-slate-700',  bg: 'bg-slate-100' },
          { label: 'Pending sign-offs',   value: stats.pendingSignoff,       color: stats.pendingSignoff > 0 ? 'text-amber-600' : 'text-green-600',
            bg: stats.pendingSignoff > 0 ? 'bg-amber-50' : 'bg-green-50' },
          { label: 'Unread notifications',value: stats.unacknowledgedNotifs, color: stats.unacknowledgedNotifs > 0 ? 'text-red-600' : 'text-green-600',
            bg: stats.unacknowledgedNotifs > 0 ? 'bg-red-50' : 'bg-green-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
            <p className="text-xs text-slate-500">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {[
          { id: 'talks',         label: `Toolbox Talks (${talks.length})`,         icon: MessageSquare },
          { id: 'notifications', label: `Notifications (${notifications.length})`, icon: Bell,
            alert: stats.unacknowledgedNotifs > 0 },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id as any)}
            className={`flex-1 flex items-center justify-center gap-2 text-sm font-medium py-2 rounded-lg transition-colors relative
              ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <t.icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t.label}</span>
            <span className="sm:hidden">{t.id === 'talks' ? 'Talks' : 'Alerts'}</span>
            {t.alert && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />}
          </button>
        ))}
      </div>

      {/* ── TAB: TOOLBOX TALKS ───────────────────────────────────── */}
      {tab === 'talks' && (
        <div className="space-y-4">
          {/* PM-08: SWMS-generated Toolbox Talk drafts (A04 WHS Officer) */}
          {(() => {
            const drafts = getToolboxDrafts().filter(d => d.status === 'DRAFT_REVIEW')
            if (drafts.length === 0) return null
            return (
              <div className="space-y-3">
                {drafts.map(draft => (
                  <div key={draft.id} className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <Zap className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-xs font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">A04</span>
                            <span className="text-xs text-amber-700 font-medium">WHS Officer — Toolbox Talk Draft</span>
                            <span className="text-xs bg-white border border-amber-200 text-amber-700 px-1.5 py-0.5 rounded-full">{draft.jobNumber}</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">{draft.title}</p>
                          <p className="text-xs text-amber-700 mt-0.5">{draft.agentNote}</p>
                          {draft.keyHazards.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              <span className="text-xs text-gray-500 flex-shrink-0">Key hazards:</span>
                              {draft.keyHazards.slice(0, 3).map((h, i) => (
                                <span key={i} className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{h.slice(0, 40)}{h.length > 40 ? '…' : ''}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => activateToolboxDraft(draft.id)}
                          className="text-xs bg-amber-600 text-white hover:bg-amber-700 px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1">
                          <CheckCircle className="w-3.5 h-3.5" />Activate
                        </button>
                        <button
                          onClick={() => activateToolboxDraft(draft.id)}
                          className="text-xs bg-white border border-amber-200 text-amber-700 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg font-medium">
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search topic, job, presenter..."
                className="input pl-9 w-full text-sm" />
            </div>
            <select value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="input text-sm">
              <option value="ALL">All projects</option>
              {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
            </select>
          </div>

          {/* Talk list */}
          {filteredTalks.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <MessageSquare className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="font-medium text-slate-500">No toolbox talks found</p>
              <p className="text-sm text-slate-400 mt-1">Conduct your first talk using the button above</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTalks.map(talk => {
                const signed   = talk.attendees.filter(a => a.acknowledgedAt).length
                const allSigned = signed === talk.attendees.length
                return (
                  <div key={talk.id} onClick={() => setSelectedTalk(talk)}
                    className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md hover:border-indigo-200 cursor-pointer transition-all">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-xl
                        ${allSigned ? 'bg-green-50' : 'bg-amber-50'}`}>
                        {allSigned ? '✅' : '📋'}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-0.5">
                              <span className="text-xs font-mono text-slate-400">{talk.talkNumber}</span>
                              <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{talk.jobNumber}</span>
                              <span className="text-xs text-slate-400">{formatDate(talk.date)}</span>
                            </div>
                            <p className="text-sm font-semibold text-slate-900 leading-snug">{talk.topic}</p>
                            <p className="text-xs text-slate-400 mt-0.5">Conducted by {talk.conductedByName}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-1" />
                        </div>

                        <div className="flex items-center gap-3 mt-3">
                          <AckBadge signed={signed} total={talk.attendees.length} />
                          {!allSigned && (
                            <span className="text-xs text-amber-600 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {talk.attendees.length - signed} pending
                            </span>
                          )}
                          {allSigned && (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <ThumbsUp className="w-3 h-3" /> All signed
                            </span>
                          )}
                        </div>

                        {/* Attendee avatars */}
                        <div className="flex items-center gap-1 mt-2">
                          {talk.attendees.slice(0, 5).map(att => (
                            <div key={att.id} title={att.workerName}
                              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 border-white
                                ${att.acknowledgedAt ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'}`}>
                              {att.workerName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </div>
                          ))}
                          {talk.attendees.length > 5 && (
                            <span className="text-xs text-slate-400 ml-1">+{talk.attendees.length - 5}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: NOTIFICATIONS ──────────────────────────────────── */}
      {tab === 'notifications' && (
        <div className="space-y-3">
          {notifications.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <Bell className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="font-medium text-slate-500">No notifications sent yet</p>
              <p className="text-sm text-slate-400 mt-1">Use the Notify Workers button to send a bulletin</p>
            </div>
          ) : (
            notifications.map(notif => {
              const meta    = SEVERITY_META[notif.severity]
              const SevIcon = meta.icon
              const ackPct  = notif.deliveredCount > 0
                ? Math.round((notif.acknowledgedCount / notif.deliveredCount) * 100) : 0

              return (
                <div key={notif.id} className={`bg-white rounded-xl border p-4 ${meta.border}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
                      <SevIcon className={`w-5 h-5 ${meta.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.text}`}>
                              {meta.label}
                            </span>
                            <span className="text-xs text-slate-400">{notif.notificationNumber}</span>
                            {notif.jobNumber && (
                              <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{notif.jobNumber}</span>
                            )}
                            {notif.smsDispatched && (
                              <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Smartphone className="w-3 h-3" /> SMS sent
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-semibold text-slate-900">{notif.title}</p>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.body}</p>
                        </div>
                      </div>

                      {/* Delivery stats */}
                      <div className="flex items-center gap-4 mt-3">
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          <Send className="w-3 h-3" />
                          Sent to {notif.deliveredCount} worker{notif.deliveredCount !== 1 ? 's' : ''}
                        </div>
                        <div className={`flex items-center gap-1.5 text-xs ${ackPct === 100 ? 'text-green-600' : 'text-amber-600'}`}>
                          <CheckCircle className="w-3 h-3" />
                          {notif.acknowledgedCount}/{notif.deliveredCount} acknowledged
                        </div>
                        <span className="text-xs text-slate-400 ml-auto">{formatRelative(notif.sentAt)}</span>
                      </div>

                      {/* Acknowledgement bar */}
                      <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${ackPct === 100 ? 'bg-green-500' : 'bg-amber-400'}`}
                          style={{ width: `${ackPct}%` }} />
                      </div>

                      <p className="text-xs text-slate-400 mt-1">
                        By {notif.sentByName} · {AUDIENCE_META[notif.audience].label}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}

      {/* ── MODALS ────────────────────────────────────────────────── */}
      {selectedTalk && (
        <TalkDetailModal
          talk={selectedTalk}
          onClose={() => setSelectedTalk(null)}
          onRemind={handleRemind}
          onAttendeeSignOff={(attId) => {
            setTalks(prev => prev.map(t =>
              t.id === selectedTalk.id
                ? { ...t, attendees: t.attendees.map(a => a.id === attId ? { ...a, acknowledgedAt: new Date().toISOString() } : a) }
                : t
            ))
          }}
        />
      )}

      {showConduct && (
        <ConductTalkModal
          onClose={() => setShowConduct(false)}
          onSubmit={talk => { handleNewTalk(talk); setSelectedTalk(talk) }}
        />
      )}

      {showNotify && (
        <SendNotificationModal
          onClose={() => setShowNotify(false)}
          onSubmit={handleNewNotification}
        />
      )}
    </div>
  )
}
